import React, { useState, useRef } from "react";
import { formatDateToYMD } from "@/lib/utils";
import { useMutation, useQuery } from "@tanstack/react-query";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { apiRequest, queryClient } from "@/lib/query-client";
import { useToast } from "@/hooks/use-toast";
import { isUnauthorizedError } from "@/lib/auth-utils";
import { formatEnum } from "@/lib/enum-utils";
import { isFileSizeValid, MAX_FILE_SIZE_MB } from "@/lib/file-utils";
import { PERIL_TYPES, PERIL_TYPE_VALUES } from "@/lib/enum-utils";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { PhoneInput } from "@/components/ui/phone-input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { X, Upload, FileText, CheckCircle, AlertCircle, Sparkles, Mail, Copy, Check } from "lucide-react";
import { QuickAssignCard } from "@/components/claims/quick-assign-card";
import { CarrierCombobox } from "@/components/carriers/carrier-combobox";
import { computeArrayCommissionPercentage, MAX_COMMISSION_PERCENTAGE } from "@/lib/commission-validation";

// Base schema with common fields
const baseClaimSchema = z.object({
  // Carrier Paperwork Information
  claimNumber: z.string().optional(),
  policyNumber: z.string().optional(),

  carrierId: z.string().nullable().optional(),

  // Policyholder Information (shared)
  policyholderEmail: z.string().email("Invalid email address").optional().or(z.literal("")),
  policyholderPhone: z.string().optional(),
  policyholderNotes: z.string().optional(),
  
  // Second Policyholder
  policyholder2FirstName: z.string().optional(),
  policyholder2LastName: z.string().optional(),
  policyholder2Email: z.string().email("Invalid email address").optional().or(z.literal("")),
  policyholder2Phone: z.string().optional(),
  

  
  // Loss Information
  dateOfLoss: z.string().min(1, "Date of loss is required"),
  perilType: z.enum(PERIL_TYPE_VALUES as unknown as [string, ...string[]], {
    required_error: "Please select a peril type",
  }),
  description: z.string().optional(),
  
  // Property Information
  propertyAddress: z.string().min(1, "Property address is required"),
  propertyCity: z.string().min(1, "City is required"),
  propertyState: z.string().min(1, "State is required"),
  propertyZip: z.string().min(1, "ZIP code is required"),
  propertyType: z.enum(["residential", "commercial", "industrial"]).optional(),
  yearBuilt: z.string().optional(),
  lenderName: z.string().optional(),
  mortgageLoanNumber: z.string().optional(),

  // Carrier Information
  claimFiledDate: z.string().optional(),

  // Paperwork Verification
  carrierPaperworkReceived: z.boolean().default(true),
  paperworkSource: z.enum(["email", "fax", "portal", "mail"]).optional(),
  dateOfLossVerified: z.boolean().default(false),
  

  
  // Process type (PA, Appraisal, Estimate Only, etc.)
  fileType: z.enum(["public_adjuster", "appraisal", "estimate", "umpire", "other"]).default("public_adjuster"),
  
  // Financial Information
  estimatedValue: z.string().optional(),
  settlementValue: z.string().optional(),
  paAgreementCompanyFeeType: z.enum(["percent", "flat_rate", "hourly"]).optional(),
  paAgreementCompanyFeePercentage: z.string().optional(),
  paAgreementCompanyFlatAmount: z.string().optional(),
  paAgreementCompanyHourlyRate: z.string().optional(),
  feeBasedOnNetIncrease: z.boolean().default(false),
  mplcEngagementDate: z.string().optional(),
});

// Residential claim schema
const residentialClaimSchema = baseClaimSchema.extend({
  claimType: z.literal("residential"),
  policyholderFirstName: z.string().trim().min(1, "First name is required"),
  policyholderLastName: z.string().trim().min(1, "Last name is required"),
  policyholderCompany: z.string().optional(),
  pointOfContact: z.string().optional(),
});

// Commercial claim schema — a commercial claim may be insured by a business OR
// by an individual, so the name/company requirement is enforced at the union
// level below ("company OR first + last name"), not on a single field.
const commercialClaimSchema = baseClaimSchema.extend({
  claimType: z.literal("commercial"),
  policyholderCompany: z.string().optional(),
  pointOfContact: z.string().optional(),
  policyholderFirstName: z.string().optional(),
  policyholderLastName: z.string().optional(),
});

// Discriminated union based on claim type
const newClaimSchema = z
  .discriminatedUnion("claimType", [residentialClaimSchema, commercialClaimSchema])
  .superRefine((data, ctx) => {
    // Commercial: require a business/company name OR the insured individual's
    // first + last name (GitHub #399). Residential already requires first+last.
    if (data.claimType === "commercial") {
      const hasCompany = !!data.policyholderCompany?.trim();
      const hasName = !!data.policyholderFirstName?.trim() && !!data.policyholderLastName?.trim();
      if (!hasCompany && !hasName) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ["policyholderCompany"],
          message: "Enter a business/company name or the insured's first and last name",
        });
      }
    }
  });

type NewClaimFormData = z.infer<typeof newClaimSchema>;

interface NewClaimModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  initialValues?: Partial<NewClaimFormData>;
  onCreated?: (id: string) => void;
}

function AutoFillHint({ field, autoFilled, currentValue }: {
  field: string;
  autoFilled: Map<string, string>;
  currentValue: string;
}) {
  if (!autoFilled.has(field)) return null;
  const original = autoFilled.get(field)!;
  const changed = currentValue !== original;
  return (
    <p className={`text-xs mt-0.5 ${changed ? "text-amber-500" : "text-blue-500"}`}>
      {changed ? `Modified (was "${original}")` : "Auto-populated from import"}
    </p>
  );
}

export default function NewClaimModal({ open, onOpenChange, initialValues, onCreated }: NewClaimModalProps) {
  const { toast } = useToast();
  
  // Upload state management - support multiple files
  const [uploadedFiles, setUploadedFiles] = useState<File[]>([]);
  const [isProcessing, setIsProcessing] = useState(false);
  const [extractedData, setExtractedData] = useState<any>(null);
  const [processingFailed, setProcessingFailed] = useState(false);
  const [processingProgress, setProcessingProgress] = useState<{ current: number; total: number } | null>(null);
  const [isDragging, setIsDragging] = useState(false);
  const estimateInputRef = useRef<HTMLInputElement>(null);

  // PA assignments state for the modal form
  const [paAssignmentsState, setPaAssignmentsState] = useState<any[]>([]);
  
  // AI Intake Questions state
  const [intakeQuestions, setIntakeQuestions] = useState<any>(null);
  const [intakeRecipient, setIntakeRecipient] = useState<'policyholder' | 'contractor'>('policyholder');
  const [emailCopied, setEmailCopied] = useState(false);
  
  // Track auto-filled fields from initialValues (for import flow)
  const [autoFilledFields, setAutoFilledFields] = useState<Map<string, string>>(new Map());

  // Query for all adjusters (no type filtering - show all in dropdowns)
  const { data: allAdjusters = [] } = useQuery<any[]>({
    queryKey: ['/api/users/adjusters'],
    enabled: open, // Only fetch when modal is open
  });
  
  // Document processing mutation
  const processDocumentMutation = useMutation({
    mutationFn: async (file: File) => {
      const formData = new FormData();
      formData.append('document', file);
      
      // apiRequest throws ApiError on non-2xx responses
      const response = await apiRequest('POST', '/api/claims/process-insurance-estimate', formData);
      return response.json();
    },
    onSuccess: (data) => {
      setExtractedData(data);
      setIsProcessing(false);
      setProcessingFailed(false);
      toast({
        title: "Document Processed",
        description: "Insurance estimate data has been extracted successfully.",
      });
      
      // Auto-fill form fields with extracted data
      autoFillFormFields(data);
    },
    onError: (error: any) => {
      setIsProcessing(false);
      setProcessingFailed(true);
      
      // Check if it's a permission error (403 status)
      const isPermissionError = error?.status === 403 || 
                                error?.message?.toLowerCase().includes('permission') || 
                                error?.message?.toLowerCase().includes('denied') ||
                                error?.message?.toLowerCase().includes('forbidden');
      
      toast({
        title: isPermissionError ? "Permission Required" : "Processing Failed",
        description: isPermissionError 
          ? "You don't have permission to process documents. Please contact your administrator."
          : "Failed to process the insurance estimate. Please try again or enter the information manually.",
        variant: "destructive",
      });
    },
  });
  
  // Auto-fill form fields with extracted data
  const autoFillFormFields = (data: any) => {
    // Set claim type first and wait for it to update
    const detectedClaimType = data.claimType || 'residential';
    form.setValue('claimType', detectedClaimType);

    // Populate whatever name/company fields were extracted, regardless of type.
    // A commercial claim can be insured by an individual, so we must keep an
    // extracted person's name instead of discarding it (GitHub #399).
    form.setValue('policyholderFirstName', data.policyholderFirstName || '');
    form.setValue('policyholderLastName', data.policyholderLastName || '');
    form.setValue('policyholderCompany', data.policyholderCompany || '');
    
    // Common fields (not claim-type dependent)
    if (data.claimNumber) form.setValue('claimNumber', data.claimNumber);
    if (data.policyNumber) form.setValue('policyNumber', data.policyNumber);

    // insuranceCarrier is now a FK carrierId — cannot autofill from OCR text
    if (data.policyholderEmail) form.setValue('policyholderEmail', data.policyholderEmail);
    if (data.policyholderPhone) form.setValue('policyholderPhone', data.policyholderPhone);
    if (data.policyholderNotes) form.setValue('policyholderNotes', data.policyholderNotes);
    if (data.pointOfContact) form.setValue('pointOfContact', data.pointOfContact);

    if (data.dateOfLoss) form.setValue('dateOfLoss', data.dateOfLoss);
    if (data.perilType) form.setValue('perilType', data.perilType);
    if (data.propertyAddress) form.setValue('propertyAddress', data.propertyAddress);
    if (data.propertyCity) form.setValue('propertyCity', data.propertyCity);
    if (data.propertyState) form.setValue('propertyState', data.propertyState.toUpperCase());
    if (data.propertyZip) form.setValue('propertyZip', data.propertyZip);
    if (data.propertyType) form.setValue('propertyType', data.propertyType);
    if (data.yearBuilt) form.setValue('yearBuilt', String(data.yearBuilt));
    if (data.description) form.setValue('description', data.description);
    if (data.claimFiledDate) form.setValue('claimFiledDate', data.claimFiledDate);
    if (data.lenderName) form.setValue('lenderName', data.lenderName);
    if (data.estimatedDamageAmount) form.setValue('estimatedValue', String(data.estimatedDamageAmount));

    // Map AI extracted claimant fields to Form Second Policyholder fields
    if (data.claimantFirstName) form.setValue('policyholder2FirstName', data.claimantFirstName);
    if (data.claimantLastName) form.setValue('policyholder2LastName', data.claimantLastName);
    if (data.claimantEmail) form.setValue('policyholder2Email', data.claimantEmail);
    if (data.claimantPhone) form.setValue('policyholder2Phone', data.claimantPhone);

    // Default Date of Loss Verification to true since it came from official docs
    if (data.dateOfLoss) form.setValue('dateOfLossVerified', true);

    // Handle Settlement Offer Value
    if (data.settlementOfferValue) {
      form.setValue('settlementValue', String(data.settlementOfferValue));
    } else if (data.estimatedDamageAmount) {
      form.setValue('settlementValue', String(data.estimatedDamageAmount));
    }

    // Apply defaults requested by user for AI Extractions
    form.setValue('paperworkSource', 'mail');
    
    // Default engagement date to today
    const today = new Date().toISOString().split('T')[0];
    form.setValue('mplcEngagementDate', today);

    // Default PA fee percentage for sales_pa to 10
    setPaAssignmentsState([{
      id: `form-${Date.now()}-${Math.random()}`,
      role: "sales_pa",
      userId: null,
      commissionPercentage: "10",
      commissionFeeType: "percent"
    }]);
  };
  
  const ALLOWED_TYPES = ['application/pdf', 'image/jpeg', 'image/jpg', 'image/png', 'text/html', 'message/rfc822'];

  // Shared validation + processing logic — called by both onChange and onDrop
  const processFiles = async (filesArray: File[]) => {
    const validFiles: File[] = [];
    for (const file of filesArray) {
      if (!ALLOWED_TYPES.includes(file.type)) {
        toast({
          title: "Invalid File Type",
          description: `${file.name} is not a supported file type. Please upload PDF, JPG, PNG, HTML, or EML files.`,
          variant: "destructive",
        });
        continue;
      }
      if (!isFileSizeValid(file)) {
        toast({
          title: "File Too Large",
          description: `${file.name} exceeds the ${MAX_FILE_SIZE_MB}MB limit.`,
          variant: "destructive",
        });
        continue;
      }
      validFiles.push(file);
    }

    if (validFiles.length === 0) return;

    setUploadedFiles(prev => [...prev, ...validFiles]);
    setIsProcessing(true);
    setProcessingFailed(false);
    setProcessingProgress({ current: 0, total: validFiles.length });

    for (let i = 0; i < validFiles.length; i++) {
      try {
        setProcessingProgress({ current: i + 1, total: validFiles.length });
        await processDocumentMutation.mutateAsync(validFiles[i]);
      } catch (error) {
        console.error(`Failed to process ${validFiles[i].name}:`, error);
      }
    }

    setIsProcessing(false);
    setProcessingProgress(null);
  };

  // Handle file upload - support multiple files (onChange path)
  const handleFileUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files;
    if (!files || files.length === 0) return;
    await processFiles(Array.from(files));
    event.target.value = '';
  };
  
  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
  };

  const handleDrop = async (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
    const files = e.dataTransfer.files;
    if (!files || files.length === 0) return;
    await processFiles(Array.from(files));
  };

  // Clear upload state
  const clearUpload = () => {
    setUploadedFiles([]);
    setIsProcessing(false);
    setExtractedData(null);
    setProcessingFailed(false);
    setProcessingProgress(null);
  };
  
  // Remove a specific file
  const removeFile = (index: number) => {
    setUploadedFiles(prev => prev.filter((_, i) => i !== index));
  };
  
  const form = useForm<NewClaimFormData>({
    resolver: zodResolver(newClaimSchema),
    defaultValues: {
      // Carrier Paperwork Information
      claimNumber: "",
      policyNumber: "",
      carrierId: null,

      // Claim Type
      claimType: "residential",
      
      // Policyholder Information
      policyholderFirstName: "",
      policyholderLastName: "",
      policyholderCompany: "",
      pointOfContact: "",
      policyholderEmail: "",
      policyholderPhone: "",
      policyholderNotes: "",
      

      
      // Loss Information
      dateOfLoss: "",
      perilType: undefined,
      description: "",
      
      // Property Information
      propertyAddress: "",
      propertyCity: "",
      propertyState: "",
      propertyZip: "",
      propertyType: undefined,
      yearBuilt: "",
      
      // Carrier Information
      claimFiledDate: "",

      // Paperwork Verification
      carrierPaperworkReceived: true,
      paperworkSource: undefined,
      dateOfLossVerified: false,
      
      // Process type
      fileType: "public_adjuster" as const,
      
      // Second Policyholder
      policyholder2FirstName: "",
      policyholder2LastName: "",
      policyholder2Email: "",
      policyholder2Phone: "",
      
      // Additional fields

      lenderName: "",
      mortgageLoanNumber: "",
      estimatedValue: "",
      settlementValue: "",
      paAgreementCompanyFeeType: "percent" as const,
      paAgreementCompanyFeePercentage: "",
      paAgreementCompanyFlatAmount: "",
      paAgreementCompanyHourlyRate: "",
      feeBasedOnNetIncrease: false,
      mplcEngagementDate: "",
    },
  });

  const companyFeeType = form.watch("paAgreementCompanyFeeType");

  // Apply initialValues when modal opens (for import pre-fill flow)
  React.useEffect(() => {
    if (initialValues && open) {
      const filled = new Map<string, string>();
      Object.entries(initialValues).forEach(([key, value]) => {
        if (value != null && value !== '') {
          form.setValue(key as any, value);
          filled.set(key, String(value));
        }
      });
      setAutoFilledFields(filled);
    }
    if (!open) {
      setAutoFilledFields(new Map());
    }
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  const createClaimMutation = useMutation({
    mutationFn: async (data: NewClaimFormData) => {
      console.log("[CREATE CLAIM] Starting mutation with data:", data);

      // Validate date of loss before proceeding
      if (!data.dateOfLoss) {
        throw new Error("Date of loss is required");
      }
      
      const dateOfLoss = data.dateOfLoss;
      if (!dateOfLoss || !/^\d{4}-\d{2}-\d{2}$/.test(dateOfLoss)) {
        throw new Error("Invalid date of loss");
      }
      
      // Create the claim with carrier paperwork data
      const claimData = {
        // Carrier paperwork information (required)
        claimNumber: data.claimNumber,
        policyNumber: data.policyNumber,
        carrierId: data.carrierId || null,

        // Claim Type
        claimType: data.claimType,
        
        // Policyholder information
        policyholderFirstName: data.policyholderFirstName || null,
        policyholderLastName: data.policyholderLastName || null,
        policyholderCompany: data.policyholderCompany || null,
        pointOfContact: data.pointOfContact || null,
        policyholderEmail: data.policyholderEmail || null,
        policyholderPhone: data.policyholderPhone || null,
        policyholderNotes: data.policyholderNotes || null,
        
        // Loss information
        dateOfLoss: dateOfLoss,
        perilType: data.perilType,
        description: data.description || null,
        
        // Property information
        propertyAddress: data.propertyAddress,
        propertyCity: data.propertyCity,
        propertyState: data.propertyState,
        propertyZip: data.propertyZip,
        propertyType: data.propertyType || null,
        yearBuilt: data.yearBuilt ? parseInt(data.yearBuilt) : null,
        
        // Carrier information
        claimFiledDate: data.claimFiledDate || null,

        
        // Second Policyholder
        policyholder2FirstName: data.policyholder2FirstName || null,
        policyholder2LastName: data.policyholder2LastName || null,
        policyholder2Email: data.policyholder2Email || null,
        policyholder2Phone: data.policyholder2Phone || null,
        
        // Process type
        fileType: data.fileType || 'public_adjuster',
        
        // Paperwork verification
        carrierPaperworkReceived: data.carrierPaperworkReceived,
        carrierPaperworkDate: data.carrierPaperworkReceived ? formatDateToYMD(new Date()) : null,
        paperworkSource: data.paperworkSource || null,
        dateOfLossVerified: data.dateOfLossVerified,
        
        // Financial Information
        estimatedValue: data.estimatedValue || null,
        settlementValue: data.settlementValue || null,
        paAgreementCompanyFeeType: data.paAgreementCompanyFeeType || null,
        paAgreementCompanyFeePercentage: data.paAgreementCompanyFeePercentage || null,
        paAgreementCompanyFlatAmount: data.paAgreementCompanyFlatAmount || null,
        paAgreementCompanyHourlyRate: data.paAgreementCompanyHourlyRate || null,
        feeBasedOnNetIncrease: data.feeBasedOnNetIncrease || false,
        mplcEngagementDate: data.mplcEngagementDate || null,
        
        // Property
        lenderName: data.lenderName || null,
        mortgageLoanNumber: data.mortgageLoanNumber || null,
      };

      const claimResponse = await apiRequest("POST", "/api/claims", claimData);
      const claim = await claimResponse.json();



      // Build PA assignments from paAssignmentsState array
      const allPaAssignments = paAssignmentsState
        .filter((a: any) => a.userId && a.userId !== "none")
        .map((a: any) => {
          const copy = { ...a };
          if (String(copy.id).startsWith("form-")) {
            delete copy.id; // Strip temp ids
          }
          return copy;
        });

      if (allPaAssignments.length > 0) {
        await apiRequest("POST", `/api/claims/${claim.id}/assign-adjusters`, { paAssignments: allPaAssignments });
      }

      // Upload all documents if any were provided (don't fail claim creation if uploads fail)
      let uploadedCount = 0;
      let failedCount = 0;
      
      if (uploadedFiles.length > 0) {
        const documentUploadResults = await Promise.allSettled(
          uploadedFiles.map(async (file) => {
            const formData = new FormData();
            formData.append('file', file);  // Changed from 'document' to 'file'
            formData.append('documentType', 'carrier_estimate');
            formData.append('description', 'Insurance company estimate uploaded during claim creation');
            formData.append('analyze', 'false'); // Skip analysis during bulk upload
            
            const response = await apiRequest('POST', `/api/claims/${claim.id}/documents`, formData);
            
            return response.json();
          })
        );
        
        // Count successes and failures
        documentUploadResults.forEach(result => {
          if (result.status === 'fulfilled') {
            uploadedCount++;
          } else {
            failedCount++;
            console.error('Document upload failed:', result.reason);
          }
        });
      }

      return { claim, uploadedCount, failedCount };
    },
    onSuccess: (data) => {
      const { uploadedCount, failedCount } = data;
      
      let description = "The new claim has been created successfully.";
      if (uploadedCount > 0 && failedCount === 0) {
        description = `Claim created successfully with ${uploadedCount} document${uploadedCount > 1 ? 's' : ''} uploaded.`;
      } else if (uploadedCount > 0 && failedCount > 0) {
        description = `Claim created. ${uploadedCount} document${uploadedCount > 1 ? 's' : ''} uploaded successfully, but ${failedCount} failed.`;
      } else if (failedCount > 0) {
        description = `Claim created, but ${failedCount} document${failedCount > 1 ? 's' : ''} failed to upload.`;
      }
      
      toast({
        title: "Claim Created",
        description,
        variant: failedCount > 0 && uploadedCount === 0 ? "destructive" : "default",
      });

      // Notify user when an existing contact was reused
      const reusedContacts = data.claim?.contactsLinked?.filter((c: any) => c.reused) ?? [];
      if (reusedContacts.length > 0) {
        const names = reusedContacts.map((c: any) => c.name).join(', ');
        toast({
          title: "Existing Contact Linked",
          description: `${names} ${reusedContacts.length === 1 ? 'was' : 'were'} already in the system and linked to this claim.`,
        });
      }

      queryClient.invalidateQueries({ queryKey: ["/api/claims"] });
      queryClient.invalidateQueries({ queryKey: ["/api/dashboard/kpis"] });
      queryClient.invalidateQueries({ queryKey: ["/api/contacts"] });

      // Notify parent about the created claim (for import auto-link flow)
      if (onCreated && data.claim?.id) {
        onCreated(data.claim.id);
      }

      // Reset form and uploaded files
      form.reset();
      setUploadedFiles([]);
      setExtractedData(null);
      setProcessingFailed(false);
      setIntakeQuestions(null);

      onOpenChange(false);
    },
    onError: (error) => {
      console.error("[CLAIM MUTATION ERROR]", error);
      if (isUnauthorizedError(error)) {
        toast({
          title: "Unauthorized",
          description: "You are logged out. Logging in again...",
          variant: "destructive",
        });
        setTimeout(() => {
          window.location.href = "/api/login";
        }, 500);
        return;
      }
      const errorMessage = error instanceof Error ? error.message : "Failed to create claim. Please try again.";
      toast({
        title: "Error",
        description: errorMessage,
        variant: "destructive",
      });
    },
  });

  // Generate AI intake questions mutation
  const generateIntakeQuestionsMutation = useMutation({
    mutationFn: async () => {
      const formData = form.getValues();
      
      const requestData = {
        claimData: {
          claimNumber: formData.claimNumber,
          policyNumber: formData.policyNumber,
          policyholderName: `${formData.policyholderFirstName} ${formData.policyholderLastName}`,
          carrierId: formData.carrierId || null,
          dateOfLoss: formData.dateOfLoss,
          perilType: formData.perilType,
          propertyAddress: `${formData.propertyAddress}, ${formData.propertyCity}, ${formData.propertyState} ${formData.propertyZip}`,
          description: formData.description,
          estimatedValue: extractedData?.totalClaimValue, // Use extracted value if available
        },
        documentsProvided: uploadedFiles.length > 0 ? ['carrier_estimate'] : [],
        recipient: intakeRecipient,
      };
      
      const response = await apiRequest("POST", "/api/ai/generate-intake-questions", requestData);
      return response.json();
    },
    onSuccess: (data) => {
      setIntakeQuestions(data);
      setEmailCopied(false); // Reset email copied state when new questions are generated
      toast({
        title: "Questions Generated",
        description: `Generated ${data.questions.length} intake questions for ${intakeRecipient}.`,
      });
    },
    onError: (error) => {
      toast({
        title: "Error",
        description: "Failed to generate intake questions. Please try again.",
        variant: "destructive",
      });
    },
  });

  const handleGenerateQuestions = () => {
    generateIntakeQuestionsMutation.mutate();
  };

  const handleCopyEmail = async () => {
    if (intakeQuestions?.emailDraft) {
      try {
        await navigator.clipboard.writeText(intakeQuestions.emailDraft);
        setEmailCopied(true);
        toast({
          title: "Copied!",
          description: "Email draft copied to clipboard.",
        });
        setTimeout(() => setEmailCopied(false), 2000);
      } catch (error) {
        console.error("Failed to copy email draft:", error);
        toast({
          title: "Error",
          description: "Failed to copy email draft. Please try again.",
          variant: "destructive",
        });
      }
    }
  };

  const handleSubmit = (data: NewClaimFormData) => {
    // Validate commission percentages don't exceed 100% (only for percent-type roles)
    const totalPct = computeArrayCommissionPercentage(paAssignmentsState);

    if (totalPct > MAX_COMMISSION_PERCENTAGE) {
      toast({
        title: "Invalid Commission Percentages",
        description: `Total commission is ${totalPct.toFixed(1)}% — cannot exceed ${MAX_COMMISSION_PERCENTAGE}%.`,
        variant: "destructive",
      });
      return;
    }

    console.log("[CLAIM SUBMISSION] Form data:", data);
    createClaimMutation.mutate(data);
  };

  const handleSubmitError = (errors: any) => {
    console.error("[CLAIM VALIDATION ERROR] Form has validation errors:", errors);
    toast({
      title: "Validation Error",
      description: "Please check all required fields are filled correctly.",
      variant: "destructive",
    });
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl max-h-[90vh] sm:max-h-[90vh] h-full sm:h-auto overflow-y-auto p-4 sm:p-6" data-testid="modal-new-claim">
        <DialogHeader>
          <DialogTitle className="text-xl font-semibold text-foreground">
            New Claim Intake
          </DialogTitle>
        </DialogHeader>

        <Form {...form}>
          <form onSubmit={form.handleSubmit(handleSubmit, handleSubmitError)} className="space-y-4 sm:space-y-6">
            {/* Insurance Estimate Upload Section */}
            <div className="bg-green-50 dark:bg-green-900/20 p-4 rounded-lg border border-green-200 dark:border-green-800">
              <h3 className="text-base sm:text-lg font-medium text-foreground mb-3 sm:mb-4 flex flex-col sm:flex-row sm:items-center">
                📋 Upload Insurance Company Estimates
                <span className="text-sm text-muted-foreground sm:ml-2">(Optional - Auto-fill form data)</span>
              </h3>
              
              <div
                className={`border-2 border-dashed rounded-lg p-4 sm:p-6 text-center cursor-pointer transition-colors ${
                  isDragging
                    ? "border-green-500 bg-green-50 dark:border-green-500 dark:bg-green-900/30"
                    : "border-green-300 dark:border-green-700"
                }`}
                onDragOver={handleDragOver}
                onDragLeave={handleDragLeave}
                onDrop={handleDrop}
                onClick={() => estimateInputRef.current?.click()}
              >
                <Upload className="mx-auto h-12 w-12 text-green-500 mb-4" />
                <div className="space-y-2">
                  <p className="text-sm font-medium text-foreground">
                    Drag and drop files here, or click to browse
                  </p>
                  <p className="text-xs text-muted-foreground">
                    Supports PDF, JPG, PNG, HTML, EML files up to {MAX_FILE_SIZE_MB}MB each
                  </p>
                </div>
                <input
                  ref={estimateInputRef}
                  type="file"
                  onChange={handleFileUpload}
                  accept=".pdf,.jpg,.jpeg,.png,.html,.htm,.eml"
                  multiple
                  className="hidden"
                  id="estimate-upload"
                  data-testid="input-estimate-upload"
                />
                <label
                  htmlFor="estimate-upload"
                  className="mt-4 inline-flex items-center px-4 py-2 bg-green-600 text-white text-sm font-medium rounded-md hover:bg-green-700 cursor-pointer"
                  onClick={(e) => e.stopPropagation()}
                >
                  <Upload className="w-4 h-4 mr-2" />
                  Choose Files
                </label>
              </div>
              
              {uploadedFiles.length > 0 && (
                <div className="mt-4 space-y-2">
                  {uploadedFiles.map((file, index) => (
                    <div key={index} className="border border-green-200 dark:border-green-800 rounded-lg p-3">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center space-x-3">
                          <FileText className="h-6 w-6 text-green-600" />
                          <div>
                            <p className="text-sm font-medium text-foreground">{file.name}</p>
                            <p className="text-xs text-muted-foreground">
                              {(file.size / 1024 / 1024).toFixed(2)} MB
                            </p>
                          </div>
                        </div>
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          onClick={() => removeFile(index)}
                          data-testid={`button-remove-file-${index}`}
                        >
                          <X className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
              
              {isProcessing && processingProgress && (
                <div className="mt-4 flex items-center space-x-2 text-blue-600">
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600"></div>
                  <span className="text-sm">Processing {processingProgress.current} of {processingProgress.total} documents...</span>
                </div>
              )}
              
              {!isProcessing && extractedData && (
                <div className="mt-4 flex items-center space-x-2 text-green-600">
                  <CheckCircle className="h-4 w-4" />
                  <span className="text-sm">Documents processed successfully - form fields have been auto-filled</span>
                </div>
              )}
              
              {!isProcessing && processingFailed && (
                <div className="mt-4 flex items-center space-x-2 text-red-600">
                  <AlertCircle className="h-4 w-4" />
                  <span className="text-sm">Some documents failed to process</span>
                </div>
              )}
            </div>

            {/* Carrier Paperwork Information */}
            <div className="bg-blue-50 dark:bg-blue-900/20 p-4 rounded-lg border border-blue-200 dark:border-blue-800">
              <h3 className="text-base sm:text-lg font-medium text-foreground mb-3 sm:mb-4 flex flex-col sm:flex-row sm:items-center">
                📋 Carrier Paperwork Information
                <span className="text-sm text-muted-foreground sm:ml-2">(Required from carrier documentation)</span>
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <FormField
                  control={form.control}
                  name="claimNumber"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>
                        Claim Number
                      </FormLabel>
                      <FormControl>
                        <Input
                          placeholder="From carrier paperwork"
                          {...field}
                          data-testid="input-claim-number"
                        />
                      </FormControl>
                      <FormMessage />
                      <AutoFillHint field="claimNumber" autoFilled={autoFilledFields} currentValue={form.watch("claimNumber") || ""} />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="policyNumber"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>
                        Policy Number
                      </FormLabel>
                      <FormControl>
                        <Input
                          placeholder="From carrier paperwork"
                          {...field}
                          data-testid="input-policy-number"
                        />
                      </FormControl>
                      <FormMessage />
                      <AutoFillHint field="policyNumber" autoFilled={autoFilledFields} currentValue={form.watch("policyNumber") || ""} />
                    </FormItem>
                  )}
                />


                <FormField
                  control={form.control}
                  name="carrierId"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Insurance Carrier</FormLabel>
                      <FormControl>
                        <CarrierCombobox
                          value={field.value}
                          onChange={field.onChange}
                          placeholder="Select or create carrier..."
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="paperworkSource"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Paperwork Source</FormLabel>
                      <Select onValueChange={field.onChange} value={field.value}>
                        <FormControl>
                          <SelectTrigger data-testid="select-paperwork-source">
                            <SelectValue placeholder="How was paperwork received?" />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          <SelectItem value="email">Email</SelectItem>
                          <SelectItem value="fax">Fax</SelectItem>
                          <SelectItem value="portal">Insurance Portal</SelectItem>
                          <SelectItem value="mail">Mail</SelectItem>
                        </SelectContent>
                      </Select>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>

              {/* Process Type */}
              <div className="mt-4">
                <FormField
                  control={form.control}
                  name="fileType"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Process Type</FormLabel>
                      <Select onValueChange={field.onChange} value={field.value}>
                        <FormControl>
                          <SelectTrigger data-testid="select-file-type">
                            <SelectValue placeholder="Select process type" />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          <SelectItem value="public_adjuster">Public Adjuster (Standard)</SelectItem>
                          <SelectItem value="appraisal">Appraisal</SelectItem>
                          <SelectItem value="estimate">Estimate Only</SelectItem>
                          <SelectItem value="umpire">Umpire</SelectItem>
                          <SelectItem value="other">Other</SelectItem>
                        </SelectContent>
                      </Select>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>
            </div>

            {/* Policyholder Information */}
            <div>
              <h3 className="text-base sm:text-lg font-medium text-foreground mb-3 sm:mb-4">
                📄 Policyholder Information
                <span className="block sm:inline text-sm text-muted-foreground sm:ml-2">(From carrier paperwork)</span>
              </h3>
              
              {/* Claim Type Selector */}
              <div className="mb-4">
                <FormField
                  control={form.control}
                  name="claimType"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>
                        Claim Type <span className="text-destructive">*</span>
                      </FormLabel>
                      <Select
                        onValueChange={(value) => {
                          // Keep any entered names/company when switching type —
                          // an individual can be the insured on a commercial
                          // claim, so clearing names would discard valid data
                          // (GitHub #399).
                          field.onChange(value);
                        }}
                        defaultValue={field.value}
                        value={field.value}
                      >
                        <FormControl>
                          <SelectTrigger data-testid="select-claim-type">
                            <SelectValue placeholder="Select claim type" />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          <SelectItem value="residential">Residential (Individual)</SelectItem>
                          <SelectItem value="commercial">Commercial (Business)</SelectItem>
                        </SelectContent>
                      </Select>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {/* Commercial: a business OR an individual can be the named
                    insured (GitHub #399), so show the company field AND the
                    person's name fields. Neither alone is hard-required — the
                    schema enforces "company OR first + last name". */}
                {form.watch('claimType') === 'commercial' ? (
                  <React.Fragment key="commercial-fields">
                    <FormField
                      control={form.control}
                      name="policyholderCompany"
                      render={({ field }) => (
                        <FormItem className="md:col-span-2">
                          <FormLabel>Business/Company Name</FormLabel>
                          <FormControl>
                            <Input {...field} value={field.value ?? ""} placeholder="e.g., ALUMNUS GARDENS, LLC" data-testid="input-policyholder-company" />
                          </FormControl>
                          <p className="text-xs text-muted-foreground mt-0.5">Company or first + last name required</p>
                          <FormMessage />
                        </FormItem>
                      )}
                    />

                    <FormField
                      control={form.control}
                      name="policyholderFirstName"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Policyholder First Name</FormLabel>
                          <FormControl>
                            <Input {...field} value={field.value ?? ""} data-testid="input-policyholder-first-name" />
                          </FormControl>
                          <FormMessage />
                          <AutoFillHint field="policyholderFirstName" autoFilled={autoFilledFields} currentValue={form.watch("policyholderFirstName") || ""} />
                        </FormItem>
                      )}
                    />

                    <FormField
                      control={form.control}
                      name="policyholderLastName"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Policyholder Last Name</FormLabel>
                          <FormControl>
                            <Input {...field} value={field.value ?? ""} data-testid="input-policyholder-last-name" />
                          </FormControl>
                          <FormMessage />
                          <AutoFillHint field="policyholderLastName" autoFilled={autoFilledFields} currentValue={form.watch("policyholderLastName") || ""} />
                        </FormItem>
                      )}
                    />

                    <FormField
                      control={form.control}
                      name="pointOfContact"
                      render={({ field }) => (
                        <FormItem className="md:col-span-2">
                          <FormLabel>Point of Contact</FormLabel>
                          <FormControl>
                            <Input {...field} value={field.value ?? ""} placeholder="Contact person's name" data-testid="input-point-of-contact" />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  </React.Fragment>
                ) : (
                  <React.Fragment key="residential-fields">
                    <FormField
                      control={form.control}
                      name="policyholderFirstName"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>
                            Policyholder First Name <span className="text-destructive">*</span>
                          </FormLabel>
                          <FormControl>
                            <Input {...field} value={field.value ?? ""} data-testid="input-policyholder-first-name" />
                          </FormControl>
                          <FormMessage />
                          <AutoFillHint field="policyholderFirstName" autoFilled={autoFilledFields} currentValue={form.watch("policyholderFirstName") || ""} />
                        </FormItem>
                      )}
                    />

                    <FormField
                      control={form.control}
                      name="policyholderLastName"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>
                            Policyholder Last Name <span className="text-destructive">*</span>
                          </FormLabel>
                          <FormControl>
                            <Input {...field} value={field.value ?? ""} data-testid="input-policyholder-last-name" />
                          </FormControl>
                          <FormMessage />
                          <AutoFillHint field="policyholderLastName" autoFilled={autoFilledFields} currentValue={form.watch("policyholderLastName") || ""} />
                        </FormItem>
                      )}
                    />
                  </React.Fragment>
                )}

                <FormField
                  control={form.control}
                  name="policyholderEmail"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Policyholder Email</FormLabel>
                      <FormControl>
                        <Input
                          type="email"
                          {...field}
                          value={field.value ?? ""}
                          data-testid="input-policyholder-email"
                        />
                      </FormControl>
                      <FormMessage />
                      <AutoFillHint field="policyholderEmail" autoFilled={autoFilledFields} currentValue={form.watch("policyholderEmail") || ""} />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="policyholderPhone"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Policyholder Phone</FormLabel>
                      <FormControl>
                        <PhoneInput
                          {...field}
                          value={field.value ?? ""}
                          data-testid="input-policyholder-phone"
                        />
                      </FormControl>
                      <FormMessage />
                      <AutoFillHint field="policyholderPhone" autoFilled={autoFilledFields} currentValue={form.watch("policyholderPhone") || ""} />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="policyholderNotes"
                  render={({ field }) => (
                    <FormItem className="md:col-span-2">
                      <FormLabel>Policyholder Notes</FormLabel>
                      <FormControl>
                        <Textarea
                          {...field}
                          placeholder="Add any notes about the policyholder..."
                          className="min-h-[80px]"
                          data-testid="input-policyholder-notes"
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>
            </div>

            {/* Second Policyholder */}
            <div>
              <h3 className="text-base sm:text-lg font-medium text-foreground mb-3 sm:mb-4">
                Second Policyholder
                <span className="block sm:inline text-sm text-muted-foreground sm:ml-2">(Optional - If there is a joint policyholder)</span>
              </h3>
              <p className="text-sm text-muted-foreground mb-4">Documents will be sent to both policyholders for signature.</p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <FormField
                  control={form.control}
                  name="policyholder2FirstName"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>First Name</FormLabel>
                      <FormControl>
                        <Input placeholder="Jane" {...field} data-testid="input-policyholder2-first-name" />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="policyholder2LastName"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Last Name</FormLabel>
                      <FormControl>
                        <Input placeholder="Doe" {...field} data-testid="input-policyholder2-last-name" />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="policyholder2Email"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Email</FormLabel>
                      <FormControl>
                        <Input type="email" placeholder="jane.doe@email.com" {...field} data-testid="input-policyholder2-email" />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="policyholder2Phone"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Phone</FormLabel>
                      <FormControl>
                        <PhoneInput {...field} data-testid="input-policyholder2-phone" />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>
            </div>



            {/* Property Information */}
            <div>
              <h3 className="text-base sm:text-lg font-medium text-foreground mb-3 sm:mb-4">Property Information</h3>
              <div className="space-y-4">
                <FormField
                  control={form.control}
                  name="propertyAddress"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>
                        Property Address <span className="text-destructive">*</span>
                      </FormLabel>
                      <FormControl>
                        <Input
                          placeholder="Street address"
                          {...field}
                          data-testid="input-property-address"
                        />
                      </FormControl>
                      <FormMessage />
                      <AutoFillHint field="propertyAddress" autoFilled={autoFilledFields} currentValue={form.watch("propertyAddress") || ""} />
                    </FormItem>
                  )}
                />

                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <FormField
                    control={form.control}
                    name="propertyCity"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>
                          City <span className="text-destructive">*</span>
                        </FormLabel>
                        <FormControl>
                          <Input {...field} data-testid="input-property-city" />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="propertyState"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>
                          State <span className="text-destructive">*</span>
                        </FormLabel>
                        <Select onValueChange={field.onChange} value={field.value}>
                          <FormControl>
                            <SelectTrigger data-testid="select-property-state">
                              <SelectValue placeholder="Select state" />
                            </SelectTrigger>
                          </FormControl>
                          <SelectContent>
                            <SelectItem value="AL">Alabama</SelectItem>
                            <SelectItem value="AK">Alaska</SelectItem>
                            <SelectItem value="AZ">Arizona</SelectItem>
                            <SelectItem value="AR">Arkansas</SelectItem>
                            <SelectItem value="CA">California</SelectItem>
                            <SelectItem value="CO">Colorado</SelectItem>
                            <SelectItem value="CT">Connecticut</SelectItem>
                            <SelectItem value="DE">Delaware</SelectItem>
                            <SelectItem value="DC">District of Columbia</SelectItem>
                            <SelectItem value="FL">Florida</SelectItem>
                            <SelectItem value="GA">Georgia</SelectItem>
                            <SelectItem value="HI">Hawaii</SelectItem>
                            <SelectItem value="ID">Idaho</SelectItem>
                            <SelectItem value="IL">Illinois</SelectItem>
                            <SelectItem value="IN">Indiana</SelectItem>
                            <SelectItem value="IA">Iowa</SelectItem>
                            <SelectItem value="KS">Kansas</SelectItem>
                            <SelectItem value="KY">Kentucky</SelectItem>
                            <SelectItem value="LA">Louisiana</SelectItem>
                            <SelectItem value="ME">Maine</SelectItem>
                            <SelectItem value="MD">Maryland</SelectItem>
                            <SelectItem value="MA">Massachusetts</SelectItem>
                            <SelectItem value="MI">Michigan</SelectItem>
                            <SelectItem value="MN">Minnesota</SelectItem>
                            <SelectItem value="MS">Mississippi</SelectItem>
                            <SelectItem value="MO">Missouri</SelectItem>
                            <SelectItem value="MT">Montana</SelectItem>
                            <SelectItem value="NE">Nebraska</SelectItem>
                            <SelectItem value="NV">Nevada</SelectItem>
                            <SelectItem value="NH">New Hampshire</SelectItem>
                            <SelectItem value="NJ">New Jersey</SelectItem>
                            <SelectItem value="NM">New Mexico</SelectItem>
                            <SelectItem value="NY">New York</SelectItem>
                            <SelectItem value="NC">North Carolina</SelectItem>
                            <SelectItem value="ND">North Dakota</SelectItem>
                            <SelectItem value="OH">Ohio</SelectItem>
                            <SelectItem value="OK">Oklahoma</SelectItem>
                            <SelectItem value="OR">Oregon</SelectItem>
                            <SelectItem value="PA">Pennsylvania</SelectItem>
                            <SelectItem value="RI">Rhode Island</SelectItem>
                            <SelectItem value="SC">South Carolina</SelectItem>
                            <SelectItem value="SD">South Dakota</SelectItem>
                            <SelectItem value="TN">Tennessee</SelectItem>
                            <SelectItem value="TX">Texas</SelectItem>
                            <SelectItem value="UT">Utah</SelectItem>
                            <SelectItem value="VT">Vermont</SelectItem>
                            <SelectItem value="VA">Virginia</SelectItem>
                            <SelectItem value="WA">Washington</SelectItem>
                            <SelectItem value="WV">West Virginia</SelectItem>
                            <SelectItem value="WI">Wisconsin</SelectItem>
                            <SelectItem value="WY">Wyoming</SelectItem>
                          </SelectContent>
                        </Select>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="propertyZip"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>
                          ZIP Code <span className="text-destructive">*</span>
                        </FormLabel>
                        <FormControl>
                          <Input {...field} data-testid="input-property-zip" />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <FormField
                    control={form.control}
                    name="propertyType"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Property Type</FormLabel>
                        <Select onValueChange={field.onChange} value={field.value}>
                          <FormControl>
                            <SelectTrigger data-testid="select-property-type">
                              <SelectValue placeholder="Select property type" />
                            </SelectTrigger>
                          </FormControl>
                          <SelectContent>
                            <SelectItem value="residential">Residential</SelectItem>
                            <SelectItem value="commercial">Commercial</SelectItem>
                            <SelectItem value="industrial">Industrial</SelectItem>
                          </SelectContent>
                        </Select>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="yearBuilt"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Year Built</FormLabel>
                        <FormControl>
                          <Input
                            type="number"
                            placeholder="YYYY"
                            min="1800"
                            max="2024"
                            {...field}
                            data-testid="input-year-built"
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>
              </div>

              <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-4">
                <FormField
                  control={form.control}
                  name="lenderName"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Mortgage/LOC Lender Name</FormLabel>
                      <FormControl>
                        <Input placeholder="e.g., Wells Fargo, Chase" {...field} data-testid="input-lender-name" />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="mortgageLoanNumber"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Mortgage/LOC Loan Number</FormLabel>
                      <FormControl>
                        <Input placeholder="e.g., 1234567890" {...field} data-testid="input-mortgage-loan-number" />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>
            </div>

            {/* Loss Information */}
            <div>
              <h3 className="text-base sm:text-lg font-medium text-foreground mb-3 sm:mb-4">🔥 Loss Information</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <FormField
                  control={form.control}
                  name="dateOfLoss"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>
                        Date of Loss <span className="text-destructive">*</span>
                      </FormLabel>
                      <FormControl>
                        <Input
                          type="date"
                          max={new Date().toISOString().split('T')[0]}
                          {...field}
                          data-testid="input-date-of-loss"
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="perilType"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>
                        Peril Type <span className="text-destructive">*</span>
                      </FormLabel>
                      <Select onValueChange={field.onChange} value={field.value}>
                        <FormControl>
                          <SelectTrigger data-testid="select-peril-type">
                            <SelectValue placeholder="Select peril type" />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          {PERIL_TYPES.map(p => (
                            <SelectItem key={p.value} value={p.value}>{p.label}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>

              <div className="mt-4">
                <FormField
                control={form.control}
                name="description"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Description of Loss</FormLabel>
                    <FormControl>
                      <Textarea
                        placeholder="Provide detailed description of the loss..."
                        rows={4}
                        {...field}
                        data-testid="textarea-description"
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>
          </div>

            {/* Claim Filed Date */}
            <div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <FormField
                  control={form.control}
                  name="claimFiledDate"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Claim Filed Date</FormLabel>
                      <FormControl>
                        <Input
                          type="date"
                          {...field}
                          data-testid="input-claim-filed-date"
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <div className="flex items-center space-x-2">
                  <FormField
                    control={form.control}
                    name="dateOfLossVerified"
                    render={({ field }) => (
                      <FormItem className="flex flex-row items-center space-x-3 space-y-0">
                        <FormControl>
                          <input
                            type="checkbox"
                            checked={field.value}
                            onChange={field.onChange}
                            data-testid="checkbox-date-verified"
                            className="rounded border border-input"
                          />
                        </FormControl>
                        <FormLabel className="text-sm font-normal">
                          Date of loss verified
                        </FormLabel>
                      </FormItem>
                    )}
                  />
                </div>
              </div>
            </div>

            {/* Public Adjuster Assignment */}
            <div className="bg-green-50 dark:bg-green-900/20 p-4 rounded-lg border border-green-200 dark:border-green-800">
              <h3 className="text-base sm:text-lg font-medium text-foreground mb-3 sm:mb-4 flex flex-col sm:flex-row sm:items-center">
                👥 Public Adjuster Assignment
                <span className="text-sm text-muted-foreground sm:ml-2">(Optional - can be assigned later)</span>
              </h3>
              <QuickAssignCard
                mode="form"
                adjusters={allAdjusters}
                claimState={form.watch("propertyState")}
                bare
                layout="grid-cols-1 md:grid-cols-2"
                assignments={paAssignmentsState}
                onAssignmentsChange={setPaAssignmentsState}
                roles={[
                  { role: "sales_pa", label: "Sales Public Adjuster", testId: "select-sales-adjuster", roleType: "sales_pa" },
                  { role: "field_pa", label: "Field Public Adjuster", testId: "select-field-adjuster", roleType: "field_pa" },
                  { role: "desk_pa", label: "Desk Public Adjuster", testId: "select-desk-adjuster", roleType: "desk_pa" },
                ]}
              />
            </div>

            {/* Financial Information */}
            <div className="bg-emerald-50 dark:bg-emerald-900/20 p-4 rounded-lg border border-emerald-200 dark:border-emerald-800">
              <h3 className="text-base sm:text-lg font-medium text-foreground mb-3 sm:mb-4">💰 Financial Information</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <FormField
                  control={form.control}
                  name="estimatedValue"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Estimated PA Value</FormLabel>
                      <FormControl>
                        <Input type="number" placeholder="0.00" {...field} data-testid="input-estimated-value" />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="settlementValue"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Settlement Offer Value</FormLabel>
                      <FormControl>
                        <Input type="number" placeholder="0.00" {...field} data-testid="input-settlement-value" />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="paAgreementCompanyFeeType"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Company Fee Type</FormLabel>
                      <Select onValueChange={field.onChange} value={field.value ?? "percent"}>
                        <FormControl>
                          <SelectTrigger data-testid="select-company-fee-type-new">
                            <SelectValue placeholder="Select fee type" />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          <SelectItem value="percent">Percentage (%)</SelectItem>
                          <SelectItem value="flat_rate">Flat Amount ($)</SelectItem>
                          <SelectItem value="hourly">Hourly ($/hr)</SelectItem>
                        </SelectContent>
                      </Select>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                {(!companyFeeType || companyFeeType === "percent") && (
                  <FormField
                    control={form.control}
                    name="paAgreementCompanyFeePercentage"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Company Fee % (PA Agreement)</FormLabel>
                        <FormControl>
                          <Input type="number" placeholder="10" {...field} data-testid="input-fee-percentage" />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                )}

                {companyFeeType === "flat_rate" && (
                  <FormField
                    control={form.control}
                    name="paAgreementCompanyFlatAmount"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Company Flat Fee ($)</FormLabel>
                        <FormControl>
                          <Input type="number" step="0.01" placeholder="500.00" {...field} data-testid="input-company-flat-amount-new" />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                )}

                {companyFeeType === "hourly" && (
                  <FormField
                    control={form.control}
                    name="paAgreementCompanyHourlyRate"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Company Hourly Rate ($/hr)</FormLabel>
                        <FormControl>
                          <Input type="number" step="0.01" placeholder="150.00" {...field} data-testid="input-company-hourly-rate-new" />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                )}

                <FormField
                  control={form.control}
                  name="mplcEngagementDate"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>MPLC Engagement Date</FormLabel>
                      <FormControl>
                        <Input type="date" {...field} data-testid="input-mplc-engagement-date" />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>

              <div className="mt-4">
                <FormField
                  control={form.control}
                  name="feeBasedOnNetIncrease"
                  render={({ field }) => (
                    <FormItem className="flex flex-row items-center gap-3">
                      <FormControl>
                        <input
                          type="checkbox"
                          checked={field.value}
                          onChange={field.onChange}
                          className="h-4 w-4 rounded border-border"
                          data-testid="checkbox-fee-net-increase"
                        />
                      </FormControl>
                      <FormLabel className="font-normal">Fee Based on Net Increase</FormLabel>
                    </FormItem>
                  )}
                />
              </div>
            </div>

            {/* AI Intake Questions Section */}
            <div className="bg-purple-50 dark:bg-purple-900/20 p-4 rounded-lg border border-purple-200 dark:border-purple-800">
              <h3 className="text-base sm:text-lg font-medium text-foreground mb-3 sm:mb-4 flex flex-col sm:flex-row sm:items-center">
                <span className="flex items-center">
                  <Sparkles className="w-5 h-5 mr-2 text-purple-500" />
                  AI-Powered Intake Questions
                </span>
                <span className="text-sm text-muted-foreground sm:ml-2">(Optional - Help gather missing info)</span>
              </h3>
              
              <div className="space-y-4">
                <div className="flex flex-col sm:flex-row sm:items-center gap-3 sm:space-x-4">
                  <div className="flex-1">
                    <label className="text-sm font-medium text-foreground mb-2 block">
                      Generate questions for:
                    </label>
                    <Select value={intakeRecipient} onValueChange={(value: 'policyholder' | 'contractor') => setIntakeRecipient(value)}>
                      <SelectTrigger data-testid="select-intake-recipient">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="policyholder">Policyholder (PH)</SelectItem>
                        <SelectItem value="contractor">Contractor (CTR)</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  
                  <div className="flex items-end">
                    <Button
                      type="button"
                      onClick={handleGenerateQuestions}
                      disabled={generateIntakeQuestionsMutation.isPending}
                      className="bg-purple-600 hover:bg-purple-700"
                      data-testid="button-generate-intake-questions"
                    >
                      {generateIntakeQuestionsMutation.isPending ? (
                        <>Generating...</>
                      ) : (
                        <>
                          <Sparkles className="w-4 h-4 mr-2" />
                          Generate Questions
                        </>
                      )}
                    </Button>
                  </div>
                </div>
                
                {/* Display generated questions */}
                {intakeQuestions && (
                  <div className="mt-4 space-y-4 border-t border-purple-200 dark:border-purple-800 pt-4">
                    {/* Missing Critical Info */}
                    {intakeQuestions.missingCriticalInfo?.length > 0 && (
                      <div className="bg-orange-50 dark:bg-orange-900/20 p-3 rounded-lg border border-orange-200 dark:border-orange-800">
                        <h4 className="text-sm font-semibold text-orange-700 dark:text-orange-300 flex items-center mb-2">
                          <AlertCircle className="w-4 h-4 mr-2" />
                          Missing Critical Information
                        </h4>
                        <ul className="list-disc list-inside text-sm text-orange-600 dark:text-orange-400 space-y-1">
                          {intakeQuestions.missingCriticalInfo.map((item: string, idx: number) => (
                            <li key={idx}>{item}</li>
                          ))}
                        </ul>
                      </div>
                    )}
                    
                    {/* Questions List */}
                    <div>
                      <h4 className="text-sm font-semibold text-foreground mb-3">
                        Generated Questions ({intakeQuestions.questions.length})
                      </h4>
                      <div className="space-y-3">
                        {intakeQuestions.questions.map((q: any, idx: number) => (
                          <div
                            key={idx}
                            className={`p-3 rounded-lg border ${
                              q.importance === 'required'
                                ? 'bg-red-50 dark:bg-red-900/20 border-red-200 dark:border-red-800'
                                : q.importance === 'recommended'
                                ? 'bg-yellow-50 dark:bg-yellow-900/20 border-yellow-200 dark:border-yellow-800'
                                : 'bg-blue-50 dark:bg-blue-900/20 border-blue-200 dark:border-blue-800'
                            }`}
                            data-testid={`intake-question-${idx}`}
                          >
                            <div className="flex items-start justify-between mb-1">
                              <span className={`text-xs font-semibold uppercase ${
                                q.importance === 'required'
                                  ? 'text-red-600 dark:text-red-400'
                                  : q.importance === 'recommended'
                                  ? 'text-yellow-600 dark:text-yellow-400'
                                  : 'text-blue-600 dark:text-blue-400'
                              }`}>
                                {q.importance} - {q.category.replace('_', ' ')}
                              </span>
                            </div>
                            <p className="text-sm text-foreground font-medium mb-1">{q.question}</p>
                            <p className="text-xs text-muted-foreground">{q.reasoning}</p>
                          </div>
                        ))}
                      </div>
                    </div>
                    
                    {/* Email Draft */}
                    {intakeQuestions.emailDraft && (
                      <div className="bg-card dark:bg-muted p-4 rounded-lg border border-border">
                        <div className="flex items-center justify-between mb-2">
                          <h4 className="text-sm font-semibold text-foreground flex items-center">
                            <Mail className="w-4 h-4 mr-2" />
                            Email Draft
                          </h4>
                          <Button
                            type="button"
                            size="sm"
                            variant="outline"
                            onClick={handleCopyEmail}
                            data-testid="button-copy-email-draft"
                          >
                            {emailCopied ? (
                              <>
                                <Check className="w-4 h-4 mr-2" />
                                Copied!
                              </>
                            ) : (
                              <>
                                <Copy className="w-4 h-4 mr-2" />
                                Copy
                              </>
                            )}
                          </Button>
                        </div>
                        <div className="text-sm text-foreground whitespace-pre-wrap bg-muted/50 dark:bg-background p-3 rounded border border-border max-h-64 overflow-y-auto">
                          {intakeQuestions.emailDraft}
                        </div>
                      </div>
                    )}
                    
                    <div className="text-xs text-muted-foreground">
                      Confidence: {Math.round(intakeQuestions.confidence * 100)}%
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Form Actions */}
            <div className="flex flex-col-reverse sm:flex-row sm:justify-end gap-2 sm:space-x-4 pt-4 sm:pt-6 border-t border-border">
              <Button
                type="button"
                variant="outline"
                onClick={() => onOpenChange(false)}
                data-testid="button-cancel"
                className="w-full sm:w-auto"
              >
                Cancel
              </Button>
              <Button
                type="submit"
                disabled={createClaimMutation.isPending}
                data-testid="button-create-claim"
                className="w-full sm:w-auto"
              >
                {createClaimMutation.isPending ? "Creating..." : "Create Claim"}
              </Button>
            </div>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
}
